import { useRef, useState } from 'react';
import type { ImagemDraft } from '../types';

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;

interface CampoImagemProps {
  id: string;
  label: string;
  help?: string;
  valor: ImagemDraft;
  onChange: (novo: ImagemDraft) => void;
  proporcao: 'paisagem' | 'retrato';
}

export default function CampoImagem({
  id,
  label,
  help,
  valor,
  onChange,
  proporcao,
}: CampoImagemProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  function aoEscolherArquivo(arquivo: File | null) {
    setErro(null);
    if (!arquivo) return;

    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Esta imagem tem mais de 5 MB. Escolha uma menor ou reduza antes de enviar.');
      return;
    }

    const url = URL.createObjectURL(arquivo);
    onChange({ arquivo, url, removida: false, carregando: false });
  }

  function remover() {
    setErro(null);
    if (valor.arquivo && valor.url) URL.revokeObjectURL(valor.url);
    onChange({ arquivo: null, url: null, removida: true, carregando: false });
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <span className="block text-base font-medium text-foreground-950">{label}</span>
      {help && <p className="mt-1 text-sm text-secondary-500">{help}</p>}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="sr-only"
        onChange={(e) => aoEscolherArquivo(e.target.files?.[0] ?? null)}
      />

      {valor.url ? (
        <div className="mt-2 overflow-hidden rounded-lg border border-secondary-200 bg-background-100">
          <div className="relative flex items-center justify-center">
            <img
              src={valor.url}
              alt={label}
              className={`w-full max-w-full h-auto object-contain ${proporcao === 'paisagem' ? 'aspect-video' : 'aspect-[3/4]'}`}
              style={{ maxHeight: '100%' }}
            />
            <button
              type="button"
              onClick={remover}
              aria-label="Remover imagem"
              className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`mt-2 flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-secondary-300 bg-white px-4 py-8 text-center transition-colors hover:border-primary-400 hover:bg-background-100 ${
            proporcao === 'paisagem' ? 'aspect-video' : 'aspect-[3/4]'
          }`}
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-image-add-line text-xl" />
          </span>
          <span className="mt-2 text-sm font-medium text-foreground-800">Escolher imagem</span>
          <span className="mt-1 text-xs text-secondary-500">jpg, png ou webp · até 5 MB</span>
        </button>
      )}

      {erro && (
        <p className="mt-1 text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}